import { Injectable } from '@angular/core';
import OidcClientData from 'models/oidc-client-data.model.ts/oidc-client-data.model';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { LogService } from '../log/log.service';
import Log from 'models/log/log.model';
import * as jwt_decode from 'jwt-decode';
import IdTokenPayload from 'models/id-token-payload/id-token-payload.model';
import { BehaviorSubject, from, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private oidcData: OidcClientData = new OidcClientData();
  private tokenRefreshSubject: BehaviorSubject<OidcClientData> = new BehaviorSubject<OidcClientData>(null);
  constructor(
    public http: HttpClient,
    public logService: LogService,
    public configService: ConfigService,
  ) {

  }

  public async handleAccessCode(code: string) {
    return new Promise((resolve: (r: OidcClientData) => any, reject: (e: Error) => any) => {
      this.http.post<any>(this.configService.getConfig().apiUrl + '/token/code/' + code + '/' + btoa(this.configService.getConfig().redirectUrl), {}, {}).subscribe({
        next: async res => {
          this.oidcData = new OidcClientData(res.data);
          await this.saveOidcData(this.oidcData);
          this.tokenRefreshSubject.next(this.oidcData);
          return resolve(this.oidcData);
        },
        error: async error => {
          console.log(error);
          await this.removeOidcData();
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TokenService.handleAccessCode',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async refreshToken(token: string) {
    return new Promise((resolve: (r: OidcClientData) => any, reject: (e: Error) => any) => {
      this.http.post<any>(this.configService.getConfig().apiUrl + '/token/refresh/' + token + '/' + btoa(this.configService.getConfig().redirectUrl), {}, {}).subscribe({
        next: async res => {
          await this.saveOidcData(new OidcClientData(res.data));
          this.tokenRefreshSubject.next(this.oidcData);
          return resolve(this.oidcData);
        },
        error: async error => {
          console.log(error);
          await this.removeOidcData();
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TokenService.refreshToken',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public checkToken(): Observable<boolean> {
    return from(new Promise(async (resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      if (!localStorage.getItem('oidcInfo')) {
        return resolve(false);
      }
      const oidcData: OidcClientData = new OidcClientData(JSON.parse(localStorage.getItem('oidcInfo')));
      if (!oidcData.idToken || !oidcData.refreshToken) {
        return resolve(false);
      }
      this.oidcData = oidcData;
      try {
        const token: IdTokenPayload = await this.decodeIdToken();
        this.oidcData = await this.refreshToken(oidcData.refreshToken);
        this.tokenRefreshSubject.next(this.oidcData);
        return resolve(true);
      } catch (ex) {
        console.log('EX:', ex.stack);
        await this.removeOidcData();
        await this.logService.create(new Log({
          description: ex.stack,
          event: 'serviceError',
          performer: 'TokenService.checkToken',
          type: 'error'
        }));
        return resolve(false);
      }
    }));
  }

  public async saveOidcData(data: OidcClientData): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      localStorage.setItem('oidcInfo', JSON.stringify(data));
      this.oidcData = data;
      return resolve(true);
    });
  }

  public async removeOidcData(): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      localStorage.clear();
      window.document.cookie = '';
      this.oidcData = new OidcClientData();
      this.tokenRefreshSubject.next(null);
      return resolve(true);
    });
  }

  public getIdToken(): string {
    return this.oidcData.idToken;
  }

  public async decodeIdToken(): Promise<IdTokenPayload> {
    return new Promise(async (resolve: (r: IdTokenPayload) => any, reject: (e: Error) => any) => {
      try {
        return resolve(new IdTokenPayload(jwt_decode(this.getIdToken())));
      } catch (ex) {
        console.log('ex:', ex);
        await this.logService.create(new Log({
          description: ex.stack,
          event: 'serviceError',
          performer: 'TokenService.checkToken',
          type: 'error'
        }));
        return reject(new Error('decodeError'));
      }
    });
  }

  public getTokenRefreshSubject(): BehaviorSubject<OidcClientData> {
    return this.tokenRefreshSubject;
  }
}
